'use client';

import React, { useState } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const COUNTRIES = [
  'Albania', 'Bosnia and Herzegovina', 'Bulgaria', 'China', 'Croatia',
  'Germany', 'Greece', 'Kosovo', 'Montenegro', 'North Macedonia',
  'Romania', 'Serbia', 'Slovenia', 'Taiwan', 'USA', 'Vietnam'
];

const DECISIVE_FACTORS = [
  'Price', 'Service quality', 'Long-standing relationship', 'Network coverage',
  'Transit times', 'Customs expertise', 'Digital tools', 'Recommendation'
];

const HOW_HEARD = [
  'Recommendation', 'WTO sales rep', 'Online / website',
  'Trade fair or event', 'Social media / press', 'Other'
];

const ONLINE_TOOLS = [
  'Quote request', 'Shipment tracking', 'MyWTO portal',
  'Documents / e-archive', 'Notifications', 'None'
];

const RATING_ITEMS = [
  'Ease of contacting our staff',
  'Speed of response to your quote requests',
  'Competitiveness of our prices',
  'Trust in our services and transit times',
  'Reaction time when unforeseen issues arise',
  'Quality of our customs representation',
  'Service-to-price ratio (overall value for money)',
  'How likely are you to recommend WTO to others?'
];

interface FormData {
  companyName: string;
  wtoOffice: string;
  country: string;
  decisiveFactors: string[];
  howHeard: string;
  onlineTools: string[];
  mostSatisfied: string;
  leastSatisfied: string;
  ratings: Record<string, number>;
  comments: string;
  contactEmail: string;
}

interface FormErrors {
  companyName?: string;
  wtoOffice?: string;
  country?: string;
  howHeard?: string;
  ratings?: Record<string, string>;
}

const initialForm: FormData = {
  companyName: '',
  wtoOffice: '',
  country: '',
  decisiveFactors: [],
  howHeard: '',
  onlineTools: [],
  mostSatisfied: '',
  leastSatisfied: '',
  ratings: {},
  comments: '',
  contactEmail: ''
};

export default function CustomerSatisfactionSurveyPage() {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [formStartedAt] = useState(() => Date.now());
  const [website, setWebsite] = useState('');

  const handleCheckbox = (field: 'decisiveFactors' | 'onlineTools', value: string) => {
    setForm(prev => {
      const arr = prev[field];
      return {
        ...prev,
        [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
      };
    });
  };

  const handleRating = (item: string, value: number) => {
    setForm(prev => ({ ...prev, ratings: { ...prev.ratings, [item]: value } }));
    setErrors(prev => {
      const ratingErrors = { ...(prev.ratings || {}) };
      delete ratingErrors[item];
      return { ...prev, ratings: ratingErrors };
    });
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.companyName.trim()) newErrors.companyName = 'Company name is required.';
    if (!form.wtoOffice.trim()) newErrors.wtoOffice = 'WTO office is required.';
    if (!form.country) newErrors.country = 'Please select a country.';
    if (!form.howHeard) newErrors.howHeard = 'Please select how you heard about WTO.';

    const ratingErrors: Record<string, string> = {};
    RATING_ITEMS.forEach(item => {
      if (!form.ratings[item]) ratingErrors[item] = 'Please provide a rating.';
    });
    if (Object.keys(ratingErrors).length > 0) newErrors.ratings = ratingErrors;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && Object.keys(ratingErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await fetch('/mail.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          formType: 'customer-survey',
          source_page: '/customer-satisfaction-survey',
          website,
          formStartedAt
        })
      });
      const responseText = await res.text();
      let data: { error?: string } = {};
      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(responseText || 'Empty response from mail server.');
      }
      if (!res.ok) throw new Error(data.error || 'Server error');
      setSubmitted(true);
    } catch (error) {
      setServerError(error instanceof Error ? error.message : 'Something went wrong, please try again or email us at info@wto.com');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (hasError?: boolean) =>
    `w-full px-4 py-2.5 rounded-md border text-sm font-body text-[#1F4E79] focus:outline-none focus:ring-2 focus:ring-[#1F4E79] transition-colors ${
      hasError
        ? 'border-red-500 bg-red-50' :'border-gray-300 bg-white focus:border-[#1F4E79]'
    }`;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#F7F9FC' }}>
      <Header />

      <main className="flex-1 pt-32 pb-12 px-4">
        <div className="max-w-[900px] mx-auto">

          {/* Hero */}
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-3 font-body" style={{ color: '#1F4E79' }}>
              Customer Satisfaction Survey
            </h1>
            <p className="text-base font-body mx-auto" style={{ color: '#4A5568' }}>
              Thank you for choosing WTO. Your feedback helps us improve our service across all 16 countries we operate in. Takes 2 minutes.
            </p>
          </div>

          {/* Confirmation */}
          {submitted ? (
            <div className="bg-white rounded-2xl shadow-sm border border-green-200 p-10 text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                style={{ background: '#D9E2F3' }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1F4E79" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold mb-3 font-body" style={{ color: '#1F4E79' }}>
                Thank you — your feedback has been received.
              </h2>
              <p className="text-base font-body" style={{ color: '#4A5568' }}>
                Your WTO contact will follow up if needed.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10 space-y-8">
              <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
                <label htmlFor="survey-website">Website</label>
                <input
                  id="survey-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>

              {/* Section 1: Company Info */}
              <div className="space-y-5">
                <h2 className="text-lg font-bold font-body pb-2 border-b" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                  Your Details
                </h2>

                {/* Company name */}
                <div>
                  <label htmlFor="companyName" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Company name <span style={{ color: '#E53E3E' }}>*</span>
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    value={form.companyName}
                    onChange={e => setForm(p => ({ ...p, companyName: e.target.value }))}
                    className={inputClass(!!errors.companyName)}
                    placeholder="Your company name"
                  />
                  {errors.companyName && <p className="mt-1 text-xs text-red-600 font-body">{errors.companyName}</p>}
                </div>

                {/* Country */}
                <div>
                  <label htmlFor="country" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Country in which you work with WTO <span style={{ color: '#E53E3E' }}>*</span>
                  </label>
                  <select
                    id="country"
                    value={form.country}
                    onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
                    className={inputClass(!!errors.country)}
                  >
                    <option value="">— Select a country —</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.country && <p className="mt-1 text-xs text-red-600 font-body">{errors.country}</p>}
                </div>

                {/* WTO office */}
                <div>
                  <label htmlFor="wtoOffice" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    WTO office (city / branch) <span style={{ color: '#E53E3E' }}>*</span>
                  </label>
                  <input
                    id="wtoOffice"
                    type="text"
                    value={form.wtoOffice}
                    onChange={e => setForm(p => ({ ...p, wtoOffice: e.target.value }))}
                    className={inputClass(!!errors.wtoOffice)}
                    placeholder="e.g. Sofia, Main Branch"
                  />
                  {errors.wtoOffice && <p className="mt-1 text-xs text-red-600 font-body">{errors.wtoOffice}</p>}
                </div>
              </div>

              {/* Section 2: Decisive factors */}
              <div>
                <fieldset>
                  <legend className="text-lg font-bold font-body pb-2 border-b w-full mb-4" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                    Decisive factor when choosing WTO
                  </legend>
                  <p className="text-sm mb-3 font-body" style={{ color: '#718096' }}>You may select more than one</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {DECISIVE_FACTORS.map(factor => (
                      <label key={factor} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={form.decisiveFactors.includes(factor)}
                          onChange={() => handleCheckbox('decisiveFactors', factor)}
                          className="w-4 h-4 rounded accent-[#1F4E79] cursor-pointer"
                        />
                        <span className="text-sm font-body" style={{ color: '#2D3748' }}>{factor}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Section 3: How heard */}
              <div>
                <fieldset>
                  <legend className="text-lg font-bold font-body pb-2 border-b w-full mb-4" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                    How did you hear about WTO? <span style={{ color: '#E53E3E' }}>*</span>
                  </legend>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {HOW_HEARD.map(option => (
                      <label key={option} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="howHeard"
                          value={option}
                          checked={form.howHeard === option}
                          onChange={() => setForm(p => ({ ...p, howHeard: option }))}
                          className="w-4 h-4 accent-[#1F4E79] cursor-pointer"
                        />
                        <span className="text-sm font-body" style={{ color: '#2D3748' }}>{option}</span>
                      </label>
                    ))}
                  </div>
                  {errors.howHeard && <p className="mt-2 text-xs text-red-600 font-body">{errors.howHeard}</p>}
                </fieldset>
              </div>

              {/* Section 4: Online tools */}
              <div>
                <fieldset>
                  <legend className="text-lg font-bold font-body pb-2 border-b w-full mb-4" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                    Which of our online tools at wto.com do you use?
                  </legend>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {ONLINE_TOOLS.map(tool => (
                      <label key={tool} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.onlineTools.includes(tool)}
                          onChange={() => handleCheckbox('onlineTools', tool)}
                          className="w-4 h-4 rounded accent-[#1F4E79] cursor-pointer"
                        />
                        <span className="text-sm font-body" style={{ color: '#2D3748' }}>{tool}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Section 5: Employee satisfaction */}
              <div className="space-y-5">
                <h2 className="text-lg font-bold font-body pb-2 border-b" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                  Employee Feedback
                </h2>
                <div>
                  <label htmlFor="mostSatisfied" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Most satisfied with which WTO employee(s)?
                  </label>
                  <input
                    id="mostSatisfied"
                    type="text"
                    value={form.mostSatisfied}
                    onChange={e => setForm(p => ({ ...p, mostSatisfied: e.target.value }))}
                    className={inputClass()}
                    placeholder="Employee name(s)"
                  />
                </div>
                <div>
                  <label htmlFor="leastSatisfied" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Least satisfied with which WTO employee(s)?
                  </label>
                  <input
                    id="leastSatisfied"
                    type="text"
                    value={form.leastSatisfied}
                    onChange={e => setForm(p => ({ ...p, leastSatisfied: e.target.value }))}
                    className={inputClass()}
                    placeholder="Employee name(s)"
                  />
                  <p className="mt-1 text-xs font-body" style={{ color: '#718096' }}>Write &apos;none&apos; if not applicable</p>
                </div>
              </div>

              {/* Section 6: Ratings */}
              <div>
                <fieldset>
                  <legend className="text-lg font-bold font-body pb-2 border-b w-full mb-2" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                    Service Ratings <span style={{ color: '#E53E3E' }}>*</span>
                  </legend>
                  <p className="text-sm mb-5 font-body" style={{ color: '#718096' }}>
                    Please rate the following (1 = very poor, 5 = excellent)
                  </p>
                  <div className="space-y-4">
                    {RATING_ITEMS.map(item => (
                      <div key={item} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 py-3 border-b last:border-0" style={{ borderColor: '#EDF2F7' }}>
                        <span className="text-sm font-body md:max-w-[55%]" style={{ color: '#2D3748' }}>{item}</span>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map(val => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => handleRating(item, val)}
                              aria-label={`Rate ${val} out of 5 for ${item}`}
                              className="w-9 h-9 rounded-full text-sm font-medium font-body transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#1F4E79]"
                              style={{
                                background: form.ratings[item] === val ? '#1F4E79' : form.ratings[item] >= val ? '#D9E2F3' : '#F7F9FC',
                                color: form.ratings[item] === val ? '#fff' : '#1F4E79',
                                border: `1.5px solid ${form.ratings[item] >= val ? '#1F4E79' : '#CBD5E0'}`
                              }}
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                        {errors.ratings?.[item] && (
                          <p className="text-xs text-red-600 font-body md:ml-2">{errors.ratings[item]}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Section 7: Comments & email */}
              <div className="space-y-5">
                <h2 className="text-lg font-bold font-body pb-2 border-b" style={{ color: '#1F4E79', borderColor: '#D9E2F3' }}>
                  Additional Information
                </h2>
                <div>
                  <label htmlFor="comments" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Comments / suggestions for improvement
                  </label>
                  <textarea
                    id="comments"
                    rows={4}
                    value={form.comments}
                    onChange={e => setForm(p => ({ ...p, comments: e.target.value }))}
                    className={`${inputClass()} resize-none`}
                    placeholder="Share any additional thoughts..."
                  />
                </div>
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium mb-1.5 font-body" style={{ color: '#2D3748' }}>
                    Contact email <span className="text-xs font-normal" style={{ color: '#718096' }}>(optional — in case we wish to follow up)</span>
                  </label>
                  <input
                    id="contactEmail"
                    type="email"
                    value={form.contactEmail}
                    onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))}
                    className={inputClass()}
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              {/* Server error */}
              {serverError && (
                <div className="rounded-lg px-4 py-3 text-sm font-body" style={{ background: '#FFF5F5', border: '1px solid #FEB2B2', color: '#C53030' }}>
                  {serverError}
                </div>
              )}

              {/* Submit */}
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-8 py-3 rounded-lg text-white text-sm font-semibold font-body transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1F4E79] disabled:opacity-70 w-full md:w-auto justify-center"
                  style={{ background: loading ? '#2D6A9F' : '#1F4E79' }}
                >
                  {loading && (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  {loading ? 'Submitting…' : 'Submit Feedback'}
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
